import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { _ as _object_without_properties } from "@swc/helpers/_/_object_without_properties";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useMemo, useState } from 'react';
import cn from 'classnames';
import { Controller, useForm } from 'react-hook-form';
import { emailValidation } from 'validators';
import { TextField, Typography, useIsMounted } from 'seagull';
import { getTestIds } from 'seagull/utils/TestIds';
import GoogleButton from '~/components/GoogleButton/GoogleButton';
import IntuitButton from '~/components/IntuitButton/IntuitButton';
import { routes } from '~/constants';
import { useIoContext, useStoreActions } from '~/contexts';
import { useLocationWithState } from '~/hooks/useLocationWithState';
import { useLocaleKeys } from '~/i18n';
import { handlePromise } from '~/infra/utils';
import { FormSeparatorHorizontal } from '~/pages/unAuthorizedPages/components/FormSeparator';
import { ErrorMessage, FooterLink, FooterText, FormContainer, FormFields, VerticalFormFields, SubmitButtonField } from '../../components/FormComponents';
import TermsAndPrivacyMessage from '../../components/TermsAndPrivacyMessage';
import XeroSignInButton from '../../components/XeroSignInButton/XeroSignInButton';
import { errorMessages } from '../../constants';
import { BiometricLoginButton } from '../BiometricLoginButton/BiometricLoginButton';
import sharedStyles from '../LoginScreen.module.scss';
import styles from './Email.module.scss';
export const GetEmailStepTID = (parentTestId)=>getTestIds({
        parentTestId,
        deprecatedTestIds: {
            EmailStepTID: {
                EmailVerifiedMsg: 'email-verified-msg',
                EmailInput: 'email-input',
                LoginButton: 'login-button',
                GoogleButton: 'google-btn-sign-in',
                ErrorMessage: 'err-message',
                SignUpLink: 'sign-up-link'
            }
        }
    });
export const loginFlowEndpoint = '/login/flow';
export default function EmailStep(_0) {
    let { onSubmit, simplified, titleMP, subtitleMP, emailFromState } = _0, props = _object_without_properties(_0, [
        "onSubmit",
        "simplified",
        "titleMP",
        "subtitleMP",
        "emailFromState"
    ]);
    const { t: emailTexts } = useLocaleKeys('pages.auth.email');
    const { EmailStepTID } = useMemo(()=>GetEmailStepTID(), []);
    const [errorMessage, setErrorMessage] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const routerLocation = useLocationWithState();
    const { afterEmailVerification } = (routerLocation === null || routerLocation === void 0 ? void 0 : routerLocation.state) || {
        afterEmailVerification: false
    };
    const { cookies, browser: { getOrigin } } = useIoContext();
    const { pathname } = routerLocation;
    const emailFromCookie = cookies.getUserEmailFromUserDetailsCookie();
    const existingEmail = emailFromState || emailFromCookie;
    const { handleSubmit, control, formState } = useForm({
        mode: 'onChange',
        defaultValues: {
            email: existingEmail || ''
        }
    });
    const { api } = useStoreActions();
    const isMounted = useIsMounted();
    async function onEmailFormSubmit(param) {
        let { email } = param;
        setErrorMessage(null);
        setIsLoading(true);
        const result = await handlePromise(api.auth.loginEmailStep(email, getOrigin() + pathname));
        if (result.success) {
            const { flow } = result.data;
            onSubmit({
                email,
                flow: flow
            });
        } else {
            setErrorMessage(errorMessages.generalLoginError);
        }
        if (isMounted()) {
            setIsLoading(false);
        }
    }
    return /*#__PURE__*/ _jsxs(FormContainer, _object_spread_props(_object_spread({}, props), {
        onSubmit: handleSubmit(onEmailFormSubmit),
        children: [
            titleMP,
            subtitleMP,
            afterEmailVerification && /*#__PURE__*/ _jsx(Typography, {
                className: sharedStyles.simplifiedEnterEmailMsg,
                skin: "success",
                testId: EmailStepTID.EmailVerifiedMsg(),
                children: emailTexts.yourAccountConfirmed()
            }),
            /*#__PURE__*/ _jsx(FormFields, {
                children: /*#__PURE__*/ _jsxs(VerticalFormFields, {
                    children: [
                        /*#__PURE__*/ _jsxs("div", {
                            children: [
                                simplified && /*#__PURE__*/ _jsx(Typography, {
                                    className: cn(sharedStyles.emailPrompt, sharedStyles.withBottomMargin, sharedStyles.centeredText),
                                    children: emailTexts.enterEmailToAccess()
                                }),
                                /*#__PURE__*/ _jsx(Controller, {
                                    control: control,
                                    name: "email",
                                    defaultValue: "",
                                    rules: emailValidation(undefined, {
                                        required: true
                                    }),
                                    render: (param)=>{
                                        let { field, fieldState } = param;
                                        var _fieldState_error;
                                        return /*#__PURE__*/ _jsx(TextField, _object_spread_props(_object_spread({
                                            bi: {
                                                name: 'login_email'
                                            },
                                            collapseHelperText: false
                                        }, field), {
                                            type: "email",
                                            label: simplified ? emailTexts.emailAddress() : emailTexts.workEmail(),
                                            error: fieldState.isTouched && !!fieldState.error,
                                            helperText: fieldState.isTouched && ((_fieldState_error = fieldState.error) === null || _fieldState_error === void 0 ? void 0 : _fieldState_error.message),
                                            testId: EmailStepTID.EmailInput(),
                                            placeholder: emailTexts.placeholder(),
                                            disabled: isLoading,
                                            autoComplete: "username"
                                        }));
                                    }
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsx(SubmitButtonField, {
                            disabled: !formState.isValid,
                            isLoading: isLoading,
                            testId: EmailStepTID.LoginButton(),
                            children: simplified ? emailTexts.sendCode() : emailTexts.signInButton()
                        }),
                        !simplified && /*#__PURE__*/ _jsxs(_Fragment, {
                            children: [
                                /*#__PURE__*/ _jsx(FormSeparatorHorizontal, {
                                    children: emailTexts.or()
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    className: styles.oauthButtonsContainer,
                                    children: [
                                        /*#__PURE__*/ _jsx(BiometricLoginButton, {}),
                                        /*#__PURE__*/ _jsx(GoogleButton, {
                                            testId: EmailStepTID.GoogleButton(),
                                            onAuthError: (msg)=>{
                                                setErrorMessage(msg || errorMessages.generalLoginError);
                                            }
                                        }),
                                        /*#__PURE__*/ _jsx(XeroSignInButton, {
                                            mode: "signIn"
                                        }),
                                        /*#__PURE__*/ _jsx(IntuitButton, {
                                            mode: "signIn"
                                        })
                                    ]
                                })
                            ]
                        })
                    ]
                })
            }),
            errorMessage && /*#__PURE__*/ _jsx(ErrorMessage, {
                testId: EmailStepTID.ErrorMessage(),
                children: errorMessage
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: sharedStyles.footer,
                children: [
                    !simplified && /*#__PURE__*/ _jsxs(FooterText, {
                        children: [
                            emailTexts.noAccount(),
                            ' ',
                            /*#__PURE__*/ _jsx(FooterLink, {
                                toInternalLink: routes.signUp,
                                testId: EmailStepTID.SignUpLink(),
                                children: emailTexts.signUpForFree()
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(TermsAndPrivacyMessage, {
                        type: "login"
                    })
                ]
            })
        ]
    }));
}
